import React, { useEffect, useState } from "react";
import axios from "axios";
import { NavLink, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "./AuthContext";
import { LogOut, FileText, Image as ImageIcon, Users, Heart, Inbox, Camera, Quote, Eye, RefreshCw, X, ExternalLink, Upload, DollarSign } from "lucide-react";
import ServicePricingAdmin from "./ServicePricingAdmin";
import AdminErrorBoundary from "./AdminErrorBoundary";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ===== Reusable bits =====
function ImageUpload({ value, onChange, auth }) {
  const [uploading, setUploading] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await axios.post(`${API}/admin/upload`, fd, auth({ headers: { "Content-Type": "multipart/form-data" } }));
      const url = `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
      onChange(url);
    } finally { setUploading(false); }
  };
  return (
    <div className="space-y-2">
      {value && <img src={value} alt="" className="h-28 w-28 object-cover rounded border border-[#E5DDD3]" />}
      <input className="admin-field" placeholder="Image URL or upload below" value={value || ""} onChange={(e) => onChange(e.target.value)} />
      <input type="file" accept="image/*" onChange={(e) => upload(e.target.files?.[0])} className="text-sm text-[#897F71]" />
      {uploading && <p className="admin-helper">Uploading…</p>}
    </div>
  );
}

function Section({ title, children, actions }) {
  return (
    <div className="admin-card p-6 md:p-8 mb-6">
      <div className="flex justify-between items-center mb-4 flex-wrap gap-3">
        <h2 className="admin-h2">{title}</h2>
        <div className="flex gap-2">{actions}</div>
      </div>
      {children}
    </div>
  );
}

// ===== Dashboard =====
function Dashboard() {
  return (
    <Section title="Welcome back">
      <p className="text-[#2A2520]/80">Use the menu to edit page content, manage portfolio &amp; vendors, curate Instagram, and view enquiries.</p>
    </Section>
  );
}

// ===== Content editor (all editable text across pages) =====
function Field({ label, path, c, upd, type, testId, helper }) {
  const get = path.split(".").reduce((o, k) => (o ? o[k] : undefined), c);
  const inner = type === "textarea"
    ? <textarea data-testid={testId} className="admin-field min-h-[100px] resize-y" value={get || ""} onChange={(e) => upd(path, e.target.value)} />
    : type === "paragraphs"
    ? <textarea data-testid={testId} className="admin-field min-h-[160px] resize-y" value={(get || []).join("\n\n")} onChange={(e) => upd(path, e.target.value.split(/\n\n+/))} />
    : type === "list"
    ? <textarea data-testid={testId} className="admin-field min-h-[120px] resize-y" value={(get || []).join("\n")} onChange={(e) => upd(path, e.target.value.split("\n").map((x) => x.trim()).filter(Boolean))} />
    : <input data-testid={testId} className="admin-field" value={get || ""} onChange={(e) => upd(path, e.target.value)} />;
  return (
    <div className={type === "paragraphs" || type === "list" ? "md:col-span-2" : ""}>
      <label className="admin-label">{label}{(type === "paragraphs") ? <span className="ml-1 text-[#897F71] font-normal">(blank line separates paragraphs)</span> : null}{type === "list" ? <span className="ml-1 text-[#897F71] font-normal">(one item per line)</span> : null}</label>
      {inner}
      {helper && <p className="admin-helper">{helper}</p>}
    </div>
  );
}

function ContentEditor() {
  const { auth } = useAuth();
  const [c, setC] = useState(null);
  const [original, setOriginal] = useState(null);
  const [status, setStatus] = useState("");
  const [lastSaved, setLastSaved] = useState(null);
  const [tab, setTab] = useState("brand");
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewPath, setPreviewPath] = useState("/");
  const iframeRef = React.useRef(null);

  useEffect(() => {
    axios.get(`${API}/content`).then((r) => { setC(r.data); setOriginal(JSON.stringify(r.data)); });
  }, []);

  // postMessage to preview iframe when content changes or drawer opens
  useEffect(() => {
    if (!previewOpen || !c || !iframeRef.current?.contentWindow) return;
    try { iframeRef.current.contentWindow.postMessage({ type: "RMM_PREVIEW_CONTENT", content: c }, "*"); } catch (_) {}
  }, [c, previewOpen, previewPath]);

  // Listen for iframe READY
  useEffect(() => {
    const onMsg = (e) => {
      if (e.data?.type === "RMM_PREVIEW_READY" && c && iframeRef.current?.contentWindow) {
        try { iframeRef.current.contentWindow.postMessage({ type: "RMM_PREVIEW_CONTENT", content: c }, "*"); } catch (_) {}
      }
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, [c]);

  // ESC to close drawer
  useEffect(() => {
    const h = (e) => { if (e.key === "Escape") setPreviewOpen(false); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, []);

  if (!c) return <p className="p-6 text-[#897F71]">Loading…</p>;

  const dirty = original !== JSON.stringify(c);
  const upd = (path, val) => {
    const next = JSON.parse(JSON.stringify(c));
    const keys = path.split("."); let o = next;
    for (let i = 0; i < keys.length - 1; i++) o = o[keys[i]] = o[keys[i]] || {};
    o[keys[keys.length - 1]] = val;
    setC(next);
  };
  const save = async () => {
    setStatus("Saving…");
    try {
      await axios.put(`${API}/admin/content`, c, auth());
      setOriginal(JSON.stringify(c));
      setLastSaved(new Date());
      setStatus("Saved");
      setTimeout(() => setStatus(""), 2000);
    } catch (e) { setStatus("Save failed"); }
  };

  const tabs = [
    { id: "brand", label: "Brand & contact" },
    { id: "home", label: "Home" },
    { id: "about", label: "About" },
    { id: "services", label: "Services" },
    { id: "portfolio", label: "Portfolio" },
    { id: "vendors", label: "Vendors I Love" },
    { id: "contact", label: "Contact" },
  ];

  const previewPages = [["/", "Home"], ["/about", "About"], ["/services", "Services"], ["/portfolio", "Portfolio"], ["/vendors", "Vendors"], ["/contact", "Contact"]];

  const statusPill = () => {
    if (status === "Saving…") return <span className="admin-eyebrow text-[#897F71]">Saving…</span>;
    if (status === "Saved") return <span className="admin-eyebrow text-emerald-700">Saved ✓</span>;
    if (status === "Save failed") return <span className="admin-eyebrow text-[#B5251A]">Save failed</span>;
    if (dirty) return <span className="admin-eyebrow text-[#B5251A]">Unsaved changes</span>;
    if (lastSaved) return <span className="admin-eyebrow text-[#897F71]">Last saved {lastSaved.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>;
    return <span className="admin-eyebrow text-[#897F71]">All changes saved</span>;
  };

  return (
    <div>
      {/* Sticky toolbar */}
      <div className="sticky top-0 z-30 -mx-4 md:-mx-6 px-4 md:px-6 py-3 bg-[#FAF6F2]/95 backdrop-blur border-b border-[#E5DDD3]">
        <div className="flex flex-wrap items-center gap-3 justify-between">
          <div>
            <h1 className="admin-h1">Page content</h1>
            <div className="mt-1">{statusPill()}</div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setPreviewOpen(true)} className="admin-btn ghost" data-testid="open-preview">
              <Eye className="h-4 w-4" /> Preview site
            </button>
            <button onClick={save} className="admin-btn" disabled={!dirty && status !== ""} data-testid="save-content">
              {status === "Saving…" ? "Saving…" : "Save all changes"}
            </button>
          </div>
        </div>
        {/* Tabs */}
        <div className="mt-4 flex flex-wrap gap-1 overflow-x-auto" role="tablist">
          {tabs.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} className={`admin-tab ${tab === t.id ? "active" : ""}`} role="tab" aria-selected={tab === t.id} data-testid={`content-tab-${t.id}`}>{t.label}</button>
          ))}
        </div>
      </div>

      {/* Tab content */}
      <div className="py-6 max-w-4xl">
        {tab === "brand" && (
          <Section title="Brand & contact details">
            <div className="grid gap-5 md:grid-cols-2">
              <Field label="Phone" path="brand.phone" c={c} upd={upd} />
              <Field label="Email" path="brand.email" c={c} upd={upd} />
              <Field label="Instagram URL" path="brand.instagram" c={c} upd={upd} helper="Full URL including https://" />
              <Field label="Instagram handle" path="brand.instagram_handle" c={c} upd={upd} helper="With the @ symbol" />
              <Field label="Location" path="brand.location" c={c} upd={upd} />
              <Field label="Footer tagline" path="brand.footer_tagline" c={c} upd={upd} helper="Short line in the footer left column" />
              <Field label="Footer credit line" path="brand.footer_credit" c={c} upd={upd} />
            </div>
          </Section>
        )}

        {tab === "home" && (
          <>
            <Section title="Hero">
              <div className="grid gap-5 md:grid-cols-2">
                <Field label="Eyebrow" path="home.hero_eyebrow" c={c} upd={upd} testId="home-hero-eyebrow" helper="Small uppercase line above the hero" />
                <Field label="Headline" path="home.hero_headline" c={c} upd={upd} testId="home-hero-headline" />
                <Field label="Primary CTA label" path="home.cta_primary" c={c} upd={upd} testId="home-cta-primary" />
                <Field label="Secondary CTA label" path="home.cta_secondary" c={c} upd={upd} testId="home-cta-secondary" />
                <div className="md:col-span-2">
                  <label className="admin-label">Hero image</label>
                  <ImageUpload value={c.home?.hero_image} onChange={(v) => upd("home.hero_image", v)} auth={auth} />
                </div>
              </div>
            </Section>
            <Section title="Marquee strip">
              <Field label="Marquee items" path="home.marquee_items" c={c} upd={upd} type="list" helper="Words that scroll across the dark band under the hero" />
            </Section>
            <Section title="About intro section">
              <div className="grid gap-5 md:grid-cols-2">
                <Field label="Eyebrow" path="home.about_intro_eyebrow" c={c} upd={upd} />
                <Field label="Headline" path="home.about_intro_headline" c={c} upd={upd} />
                <Field label="Paragraphs" path="home.about_intro_paragraphs" c={c} upd={upd} type="paragraphs" />
                <Field label="CTA label" path="home.about_intro_cta" c={c} upd={upd} />
              </div>
            </Section>
            <Section title="Services teaser">
              <div className="grid gap-5 md:grid-cols-2">
                <Field label="Eyebrow" path="home.services_eyebrow" c={c} upd={upd} />
                <Field label="Headline" path="home.services_headline" c={c} upd={upd} />
                <Field label="CTA label" path="home.services_cta" c={c} upd={upd} />
              </div>
            </Section>
            <Section title="Portfolio teaser">
              <div className="grid gap-5 md:grid-cols-2">
                <Field label="Eyebrow" path="home.portfolio_eyebrow" c={c} upd={upd} />
                <Field label="Headline" path="home.portfolio_headline" c={c} upd={upd} />
                <Field label="CTA label" path="home.portfolio_cta" c={c} upd={upd} />
              </div>
            </Section>
            <Section title="Testimonials section">
              <div className="grid gap-5 md:grid-cols-2">
                <Field label="Eyebrow" path="home.testimonials_eyebrow" c={c} upd={upd} />
                <Field label="Headline" path="home.testimonials_headline" c={c} upd={upd} />
              </div>
            </Section>
            <Section title="Vendors teaser">
              <div className="grid gap-5 md:grid-cols-2">
                <Field label="Eyebrow" path="home.vendors_eyebrow" c={c} upd={upd} />
                <Field label="Headline" path="home.vendors_headline" c={c} upd={upd} />
                <Field label="Paragraph" path="home.vendors_paragraph" c={c} upd={upd} type="textarea" />
                <Field label="CTA label" path="home.vendors_cta" c={c} upd={upd} />
              </div>
            </Section>
            <Section title="Instagram section">
              <div className="grid gap-5 md:grid-cols-2">
                <Field label="Eyebrow" path="home.instagram_eyebrow" c={c} upd={upd} />
                <Field label="Headline" path="home.instagram_headline" c={c} upd={upd} />
              </div>
            </Section>
            <Section title="Final CTA">
              <div className="grid gap-5 md:grid-cols-2">
                <Field label="Eyebrow" path="home.final_cta_eyebrow" c={c} upd={upd} />
                <Field label="Headline" path="home.final_cta_headline" c={c} upd={upd} />
                <Field label="Paragraph" path="home.final_cta_paragraph" c={c} upd={upd} type="textarea" />
                <Field label="Button label" path="home.final_cta_button" c={c} upd={upd} />
              </div>
            </Section>
          </>
        )}

        {tab === "about" && (
          <>
            <Section title="Hero">
              <div className="grid gap-5 md:grid-cols-2">
                <Field label="Eyebrow" path="about.eyebrow" c={c} upd={upd} />
                <Field label="Headline" path="about.headline" c={c} upd={upd} />
                <div className="md:col-span-2">
                  <label className="admin-label">Portrait image</label>
                  <ImageUpload value={c.about?.portrait_image} onChange={(v) => upd("about.portrait_image", v)} auth={auth} />
                </div>
                <Field label="Tools caption" path="about.tools_caption" c={c} upd={upd} helper="Small label next to the comb/lipstick icons" />
                <Field label="Bio CTA label" path="about.cta" c={c} upd={upd} />
              </div>
            </Section>
            <Section title="Bio">
              <Field label="Paragraphs" path="about.paragraphs" c={c} upd={upd} type="paragraphs" />
            </Section>
            <Section title="Credentials">
              <div className="grid gap-5 md:grid-cols-2">
                <Field label="Eyebrow" path="about.credentials_eyebrow" c={c} upd={upd} />
                <Field label="Headline" path="about.credentials_headline" c={c} upd={upd} />
                <div className="md:col-span-2">
                  <label className="admin-label">Credentials list <span className="font-normal text-[#897F71]">(one per line, format: Title | Text)</span></label>
                  <textarea className="admin-field min-h-[140px] resize-y" value={(c.about?.credentials || []).map((x) => `${x.title} | ${x.text}`).join("\n")} onChange={(e) => upd("about.credentials", e.target.value.split("\n").filter(Boolean).map((l) => { const [title, ...rest] = l.split("|"); return { title: (title || "").trim(), text: rest.join("|").trim() }; }))} />
                  <p className="admin-helper">Example: <code>Diploma | Specialised Makeup Services</code></p>
                </div>
              </div>
            </Section>
            <Section title="Instagram section">
              <div className="grid gap-5 md:grid-cols-2">
                <Field label="Eyebrow" path="about.instagram_eyebrow" c={c} upd={upd} />
                <Field label="Headline" path="about.instagram_headline" c={c} upd={upd} />
              </div>
            </Section>
          </>
        )}

        {tab === "services" && (
          <>
            <Section title="Page header">
              <div className="grid gap-5 md:grid-cols-2">
                <Field label="Eyebrow" path="services_page.eyebrow" c={c} upd={upd} />
                <Field label="Headline" path="services_page.headline" c={c} upd={upd} />
                <Field label="Intro paragraph" path="services_page.intro" c={c} upd={upd} type="textarea" />
              </div>
            </Section>
            <Section title="Final CTA section">
              <div className="grid gap-5 md:grid-cols-2">
                <Field label="Headline" path="services_page.final_headline" c={c} upd={upd} />
                <Field label="CTA label" path="services_page.final_cta" c={c} upd={upd} />
                <Field label="Paragraph" path="services_page.final_paragraph" c={c} upd={upd} type="textarea" />
              </div>
              <p className="admin-helper mt-3">Individual service cards (title, tagline, description, inclusions) live under the <b>Services</b> menu in the sidebar.</p>
            </Section>
          </>
        )}

        {tab === "portfolio" && (
          <Section title="Portfolio page">
            <div className="grid gap-5 md:grid-cols-2">
              <Field label="Eyebrow" path="portfolio_page.eyebrow" c={c} upd={upd} />
              <Field label="Headline" path="portfolio_page.headline" c={c} upd={upd} />
              <Field label="Intro paragraph" path="portfolio_page.intro" c={c} upd={upd} type="textarea" />
              <Field label="Empty-state message" path="portfolio_page.empty_message" c={c} upd={upd} helper="Shown when a category has no items" />
            </div>
          </Section>
        )}

        {tab === "vendors" && (
          <>
            <Section title="Page header">
              <div className="grid gap-5 md:grid-cols-2">
                <Field label="Eyebrow" path="vendors_page.eyebrow" c={c} upd={upd} />
                <Field label="Headline" path="vendors_page.headline" c={c} upd={upd} />
                <Field label="Intro paragraph" path="vendors_page.intro" c={c} upd={upd} type="textarea" />
                <Field label="Empty-state message" path="vendors_page.empty_message" c={c} upd={upd} />
              </div>
            </Section>
            <Section title="Final section">
              <div className="grid gap-5 md:grid-cols-2">
                <Field label="Headline" path="vendors_page.final_headline" c={c} upd={upd} />
                <Field label="Paragraph" path="vendors_page.final_paragraph" c={c} upd={upd} type="textarea" />
              </div>
            </Section>
          </>
        )}

        {tab === "contact" && (
          <>
            <Section title="Page header">
              <div className="grid gap-5 md:grid-cols-2">
                <Field label="Eyebrow" path="contact_page.eyebrow" c={c} upd={upd} />
                <Field label="Headline" path="contact_page.headline" c={c} upd={upd} />
                <Field label="Intro paragraph" path="contact_page.intro" c={c} upd={upd} type="textarea" />
              </div>
            </Section>
            <Section title="Form labels & messages">
              <div className="grid gap-5 md:grid-cols-2">
                <Field label="Submit button label" path="contact_page.submit_label" c={c} upd={upd} />
                <Field label="Footer note (under button)" path="contact_page.footer_note" c={c} upd={upd} />
                <Field label="Success message" path="contact_page.success_message" c={c} upd={upd} type="textarea" />
                <Field label="Error message" path="contact_page.error_message" c={c} upd={upd} type="textarea" />
              </div>
            </Section>
          </>
        )}
      </div>

      {/* Slide-over preview drawer */}
      {previewOpen && (
        <>
          <div className="preview-drawer-backdrop" onClick={() => setPreviewOpen(false)} data-testid="preview-backdrop" />
          <div className="preview-drawer" data-testid="preview-drawer">
            <div className="flex flex-wrap items-center gap-2 p-3 border-b border-[#E5DDD3] bg-white">
              <span className="admin-eyebrow mr-2">Live preview</span>
              <div className="flex flex-wrap gap-1" data-testid="preview-pages">
                {previewPages.map(([p, l]) => (
                  <button key={p} onClick={() => setPreviewPath(p)} className={`admin-tab text-xs px-3 py-1 ${previewPath === p ? "active" : ""}`} data-testid={`preview-tab-${l.toLowerCase()}`}>{l}</button>
                ))}
              </div>
              <div className="ml-auto flex gap-1">
                <button onClick={() => iframeRef.current && (iframeRef.current.src = iframeRef.current.src)} className="admin-btn ghost text-xs" title="Refresh preview" data-testid="preview-refresh"><RefreshCw className="h-3.5 w-3.5" /></button>
                <a href={previewPath} target="_blank" rel="noreferrer" className="admin-btn ghost text-xs" title="Open in new tab" data-testid="preview-open-tab"><ExternalLink className="h-3.5 w-3.5" /></a>
                <button onClick={() => setPreviewOpen(false)} className="admin-btn ghost text-xs" title="Close" data-testid="preview-close"><X className="h-3.5 w-3.5" /></button>
              </div>
            </div>
            <iframe
              ref={iframeRef}
              src={previewPath}
              title="Live preview"
              className="flex-1 w-full bg-cream"
              data-testid="preview-iframe"
              onLoad={() => { if (c && iframeRef.current?.contentWindow) { try { iframeRef.current.contentWindow.postMessage({ type: "RMM_PREVIEW_CONTENT", content: c }, "*"); } catch (_) {} } }}
            />
          </div>
        </>
      )}
    </div>
  );
}

// ===== Generic list CRUD =====
function useCrud(path, listPath, opts = {}) {
  const { auth } = useAuth();
  const [items, setItems] = useState([]);
  const load = () => axios.get(`${API}/${listPath || path}`, opts.adminList ? auth() : undefined).then((r) => setItems(r.data || []));
  useEffect(() => { load(); }, []);
  const save = async (item) => { await axios.post(`${API}/admin/${path}`, item, auth()); load(); };
  const del = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    try { await axios.delete(`${API}/admin/${path}/${id}`, auth()); load(); }
    catch (e) { throw e; }
  };
  return { items, save, del, reload: load, auth };
}

function ItemForm({ fields, value, onSave, onCancel, auth, requiredKey }) {
  // Defensive: normalise legacy/incomplete records so a missing or null field
  // never causes the form to crash (e.g. older portfolio rows without
  // `focal_point`, `alt`, `category` or `published`).
  const initial = React.useMemo(() => {
    const safe = { ...(value || {}) };
    (fields || []).forEach((f) => {
      if (safe[f.key] === undefined || safe[f.key] === null) {
        if (f.type === "list") safe[f.key] = [];
        else if (f.type === "boolean") safe[f.key] = false;
        else if (f.type === "number") safe[f.key] = "";
        else safe[f.key] = "";
      }
    });
    return safe;
  }, [value, fields]);

  const [v, setV] = useState(initial);
  const [err, setErr] = useState("");
  const set = (k, val) => setV({ ...v, [k]: val });
  const canSave = !requiredKey || (v[requiredKey] && String(v[requiredKey]).trim().length > 0);
  const handleSave = async () => {
    setErr("");
    try { await onSave(v); }
    catch (e) { setErr(e?.response?.data?.detail || "Save failed"); }
  };

  // Support both string options ("Bridal") and object options ({ value, label }).
  const renderOptions = (options) => (options || [])
    .filter((o) => o !== undefined && o !== null)
    .map((o, i) => {
      if (typeof o === "object") {
        const val = o.value ?? "";
        const label = o.label ?? String(val);
        return <option key={`${val}-${i}`} value={val}>{label}</option>;
      }
      const str = String(o);
      return <option key={`${str}-${i}`} value={str}>{str}</option>;
    });

  return (
    <div className="border border-[#E5DDD3] bg-[#FDFBF8] rounded-lg p-5 mb-4 space-y-3">
      {fields.map((f) => (
        <div key={f.key}>
          <label className="admin-label">{f.label}{f.key === requiredKey ? " *" : ""}</label>
          {f.type === "textarea" ? <textarea className="admin-field min-h-[100px] resize-y" value={v[f.key] || ""} onChange={(e) => set(f.key, e.target.value)} />
            : f.type === "image" ? <ImageUpload value={v[f.key]} onChange={(val) => set(f.key, val)} auth={auth} />
            : f.type === "list" ? <textarea className="admin-field min-h-[80px] resize-y" value={(Array.isArray(v[f.key]) ? v[f.key] : []).join("\n")} onChange={(e) => set(f.key, e.target.value.split("\n").filter(Boolean))} />
            : f.type === "select" ? <select className="admin-field" value={v[f.key] ?? ""} onChange={(e) => set(f.key, e.target.value)}><option value="">— select —</option>{renderOptions(f.options)}</select>
            : f.type === "boolean" ? (
                <label className="flex items-center gap-2 cursor-pointer select-none"><input type="checkbox" checked={!!v[f.key]} onChange={(e) => set(f.key, e.target.checked)} className="h-4 w-4 accent-coral" /> <span className="text-sm text-[#2A2520]">{f.checkboxLabel || "Yes"}</span></label>
              )
            : <input className="admin-field" type={f.type || "text"} value={v[f.key] ?? ""} onChange={(e) => set(f.key, e.target.value)} />
          }
          {f.helper && <p className="admin-helper mt-1">{f.helper}</p>}
        </div>
      ))}
      {err && <p className="border border-[#B5251A] bg-[#FFF1EE] text-[#B5251A] rounded-md p-2 text-sm" data-testid="form-error">{typeof err === 'string' ? err : JSON.stringify(err)}</p>}
      <div className="flex gap-2 pt-2">
        <button className="admin-btn" disabled={!canSave} onClick={handleSave} data-testid="item-save">Save</button>
        <button className="admin-btn ghost" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

function CrudList({ title, path, listPath, fields, render, getCategories, requiredKey, extraActions, defaults, headerExtra, adminList, sortable }) {
  const { items, save, del, auth, reload } = useCrud(path, listPath, { adminList });
  const [editing, setEditing] = useState(null);
  const [delErr, setDelErr] = useState("");
  const [localOrder, setLocalOrder] = useState(null);
  const dragId = React.useRef(null);
  const cats = getCategories ? getCategories() : null;
  const fieldsWithCats = cats ? fields.map((f) => f.key === "category" ? { ...f, type: "select", options: cats } : f) : fields;
  const handleDelete = async (id) => {
    setDelErr("");
    try { await del(id); }
    catch (e) { setDelErr(e?.response?.data?.detail || "Delete failed"); }
  };

  const displayItems = localOrder || items;

  const persistOrder = async (ordered) => {
    try {
      const payload = ordered.map((it, i) => ({ id: it.id, order: i }));
      await axios.post(`${API}/admin/${path}/reorder`, payload, auth());
      reload();
      setLocalOrder(null);
    } catch (e) {
      // fall back to individual saves
      try {
        await Promise.all(ordered.map((it, i) => save({ ...it, order: i })));
      } catch (_) {}
      setLocalOrder(null);
    }
  };

  const onDragStart = (id) => () => { dragId.current = id; };
  const onDragOver = (e) => { e.preventDefault(); };
  const onDrop = (targetId) => () => {
    if (!dragId.current || dragId.current === targetId) return;
    const from = displayItems.findIndex((x) => x.id === dragId.current);
    const to = displayItems.findIndex((x) => x.id === targetId);
    if (from < 0 || to < 0) return;
    const next = [...displayItems];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    setLocalOrder(next);
    dragId.current = null;
    persistOrder(next);
  };

  return (
    <Section title={title} actions={<>{headerExtra}<button className="admin-btn" onClick={() => setEditing(defaults || {})} data-testid={`add-${path}`}>+ Add new</button></>}>
      {sortable && displayItems.length > 1 && (
        <p className="admin-helper mb-3">Drag the <span className="font-semibold">⋮⋮</span> handle on the left of any row to reorder. Changes save automatically.</p>
      )}
      {editing !== null && (
        <ItemForm
          fields={fieldsWithCats}
          value={editing}
          auth={auth}
          requiredKey={requiredKey}
          onSave={async (v) => { await save(v); setEditing(null); }}
          onCancel={() => setEditing(null)}
        />
      )}
      {delErr && <p className="border border-[#B5251A] bg-[#FFF1EE] text-[#B5251A] rounded-md p-2 text-sm mb-3" data-testid="delete-error">{delErr}</p>}
      <div className="space-y-2.5">
        {displayItems.map((it) => (
          <div
            key={it.id}
            className="border border-[#E5DDD3] bg-white rounded-md p-4 flex flex-wrap items-center justify-between gap-3"
            data-testid={`row-${path}-${it.id}`}
            draggable={!!sortable}
            onDragStart={sortable ? onDragStart(it.id) : undefined}
            onDragOver={sortable ? onDragOver : undefined}
            onDrop={sortable ? onDrop(it.id) : undefined}
          >
            <div className="flex items-center gap-3 flex-1 min-w-0">
              {sortable && (
                <span className="cursor-grab active:cursor-grabbing text-[#897F71] hover:text-ink select-none" aria-label="Drag to reorder" title="Drag to reorder" data-testid={`drag-handle-${it.id}`}>⋮⋮</span>
              )}
              <div className="flex-1 min-w-0">{render ? render(it) : <span>{it.name || it.title}</span>}</div>
            </div>
            <div className="flex gap-2 flex-wrap">
              {extraActions && extraActions(it, { save, reload })}
              <button className="admin-btn ghost" onClick={() => setEditing(it)}>Edit</button>
              <button className="admin-btn danger" onClick={() => handleDelete(it.id)}>Delete</button>
            </div>
          </div>
        ))}
        {displayItems.length === 0 && <p className="text-[#897F71]">No items yet.</p>}
      </div>
    </Section>
  );
}

// ===== Bulk image upload (Portfolio) =====
function prettifyFilename(name) {
  return (name || "").replace(/\.[^.]+$/, "").replace(/[-_]+/g, " ").replace(/\s+/g, " ").trim().replace(/\b\w/g, (c) => c.toUpperCase()) || "Untitled";
}

function BulkUpload({ catNames, onComplete, auth }) {
  const [queue, setQueue] = useState([]);
  const [defaultCategory, setDefaultCategory] = useState("");
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const inputRef = React.useRef(null);

  const addFiles = (newFiles) => {
    const queued = Array.from(newFiles)
      .filter((f) => f.type.startsWith("image/"))
      .map((f) => ({ id: Math.random().toString(36).slice(2), file: f, title: prettifyFilename(f.name), status: "pending", progress: 0 }));
    if (queued.length === 0) return;
    setQueue((q) => [...q, ...queued]);
  };

  const onDrop = (e) => { e.preventDefault(); setDragActive(false); addFiles(e.dataTransfer.files); };
  const updateItem = (id, patch) => setQueue((q) => q.map((x) => x.id === id ? { ...x, ...patch } : x));
  const removeItem = (id) => setQueue((q) => q.filter((x) => x.id !== id));
  const clearDone = () => setQueue((q) => q.filter((x) => x.status !== "done"));

  const startUpload = async () => {
    const pending = queue.filter((x) => x.status === "pending");
    if (pending.length === 0) return;
    setUploading(true);
    let any = false;
    for (const item of pending) {
      updateItem(item.id, { status: "uploading", progress: 0, error: "" });
      try {
        const fd = new FormData(); fd.append("file", item.file);
        const { data } = await axios.post(`${API}/admin/upload`, fd, auth({
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (e) => {
            const pct = Math.round((e.loaded / (e.total || e.loaded)) * 100);
            updateItem(item.id, { progress: pct });
          },
        }));
        const url = `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
        await axios.post(`${API}/admin/portfolio`, {
          title: item.title?.trim() || "Untitled",
          category: defaultCategory || "",
          alt: item.title?.trim() || "",
          image: url,
          published: false,
        }, auth());
        updateItem(item.id, { status: "done", progress: 100 });
        any = true;
      } catch (e) {
        updateItem(item.id, { status: "error", error: e?.response?.data?.detail || "Upload failed" });
      }
    }
    setUploading(false);
    if (any) onComplete?.();
  };

  const counts = queue.reduce((a, x) => { a[x.status] = (a[x.status] || 0) + 1; return a; }, {});
  const pendingCount = counts.pending || 0;

  return (
    <div className="admin-card p-6 md:p-8 mb-6" data-testid="bulk-upload">
      <div className="flex justify-between items-start mb-4 flex-wrap gap-3">
        <div>
          <h2 className="admin-h2">Bulk image upload</h2>
          <p className="admin-helper mt-1">Drop multiple images — one portfolio item is created per image. New items are saved as <b>drafts</b> and won&apos;t appear on the public site until you publish them.</p>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-[1fr_auto] md:items-end mb-4">
        <div>
          <label className="admin-label" htmlFor="bulk-default-category">Default category for new items</label>
          <select id="bulk-default-category" className="admin-field" value={defaultCategory} onChange={(e) => setDefaultCategory(e.target.value)} data-testid="bulk-default-category">
            <option value="">— None (you can set per-item later) —</option>
            {catNames.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <p className="admin-helper">You can change the category on individual items below before or after upload.</p>
        </div>
      </div>

      <div
        onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
        onDragLeave={() => setDragActive(false)}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        className={`rounded-lg border-2 border-dashed text-center py-12 px-6 cursor-pointer transition-colors ${dragActive ? "border-coral bg-[#FFF1EC]" : "border-[#D7CFC3] bg-[#FDFBF8] hover:bg-[#F5EFE7]"}`}
        data-testid="bulk-dropzone"
      >
        <Upload className="h-7 w-7 mx-auto text-[#897F71]" />
        <p className="mt-3 font-semibold text-[#2A2520]">Drag &amp; drop images here</p>
        <p className="admin-helper">or click to choose files (PNG, JPG, WEBP — up to 6 MB each)</p>
        <input ref={inputRef} type="file" multiple accept="image/*" className="hidden" onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} data-testid="bulk-file-input" />
      </div>

      {queue.length > 0 && (
        <div className="mt-5">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
            <p className="admin-eyebrow">{queue.length} file{queue.length === 1 ? "" : "s"} in queue
              {counts.uploading ? ` · ${counts.uploading} uploading` : ""}
              {counts.done ? ` · ${counts.done} done` : ""}
              {counts.error ? ` · ${counts.error} failed` : ""}
            </p>
            <div className="flex gap-2">
              {counts.done ? <button className="admin-btn ghost" onClick={clearDone} data-testid="bulk-clear-done">Clear completed</button> : null}
              <button className="admin-btn" onClick={startUpload} disabled={uploading || pendingCount === 0} data-testid="bulk-upload-start">
                {uploading ? "Uploading…" : `Upload ${pendingCount} file${pendingCount === 1 ? "" : "s"}`}
              </button>
            </div>
          </div>
          <div className="space-y-2">
            {queue.map((it) => (
              <div key={it.id} className="border border-[#E5DDD3] bg-white rounded-md p-3 flex flex-wrap items-center gap-3" data-testid={`bulk-row-${it.id}`}>
                <img src={URL.createObjectURL(it.file)} alt="" className="h-12 w-12 object-cover rounded border border-[#E5DDD3] shrink-0" />
                <div className="flex-1 min-w-[200px]">
                  <input
                    className="admin-field"
                    value={it.title}
                    onChange={(e) => updateItem(it.id, { title: e.target.value })}
                    disabled={it.status === "uploading"}
                    data-testid={`bulk-title-${it.id}`}
                  />
                  {it.status === "uploading" && (
                    <div className="mt-2 h-1.5 w-full bg-[#F5EFE7] rounded overflow-hidden">
                      <div className="h-full bg-coral transition-all" style={{ width: `${it.progress}%` }} />
                    </div>
                  )}
                  {it.status === "error" && <p className="text-xs text-[#B5251A] mt-1">{it.error}</p>}
                </div>
                <div className="text-xs min-w-[70px] text-right">
                  {it.status === "pending" && <span className="text-[#897F71]">Pending</span>}
                  {it.status === "uploading" && <span className="text-[#897F71]">{it.progress}%</span>}
                  {it.status === "done" && <span className="text-emerald-700 font-semibold">✓ Uploaded</span>}
                  {it.status === "error" && <span className="text-[#B5251A] font-semibold">Failed</span>}
                </div>
                {it.status !== "uploading" && (
                  <button onClick={() => removeItem(it.id)} className="admin-btn ghost text-xs" data-testid={`bulk-remove-${it.id}`}><X className="h-3.5 w-3.5" /></button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ===== Portfolio =====
function PortfolioAdmin() {
  const [cats, setCats] = useState([]);
  const [reloadKey, setReloadKey] = useState(0);
  const { auth } = useAuth();
  useEffect(() => { axios.get(`${API}/portfolio-categories`).then((r) => setCats(r.data || [])); }, []);
  const catNames = cats.map((c) => c.name);
  return (
    <>
      <CrudList
        title="Portfolio categories"
        path="portfolio-categories"
        requiredKey="name"
        fields={[{ key: "name", label: "Category name" }, { key: "order", label: "Order", type: "number" }]}
        render={(it) => <span className="admin-h2">{it.name}</span>}
      />
      <BulkUpload catNames={catNames} auth={auth} onComplete={() => setReloadKey((k) => k + 1)} />
      <CrudList
        key={reloadKey}
        title="Portfolio items"
        path="portfolio"
        listPath="admin/portfolio"
        adminList
        requiredKey="title"
        getCategories={() => catNames}
        defaults={{ published: true }}
        fields={[
          { key: "title", label: "Title" },
          { key: "category", label: "Category" },
          { key: "alt", label: "Alt text (for screen readers)" },
          { key: "image", label: "Image", type: "image" },
          { key: "focal_point", label: "Image framing (focal point)", type: "select", options: [
              { value: "50% 18%", label: "Top (face / head) — recommended" },
              { value: "50% 30%", label: "Upper" },
              { value: "50% 50%", label: "Center" },
              { value: "50% 70%", label: "Lower" },
              { value: "50% 90%", label: "Bottom" },
              { value: "20% 50%", label: "Left" },
              { value: "80% 50%", label: "Right" },
            ], helper: "Defaults to face-safe (top). Use bottom/left/right if a face is being cropped awkwardly." },
          { key: "published", label: "Publish on the public site", type: "boolean", checkboxLabel: "Visible to visitors (uncheck to keep as draft)" },
          { key: "order", label: "Order", type: "number" },
        ]}
        sortable
        extraActions={(it, { save }) => (
          <button
            className="admin-btn ghost"
            onClick={() => save({ ...it, published: it.published === false })}
            data-testid={`toggle-publish-${it.id}`}
          >
            {it.published === false ? "Publish" : "Unpublish"}
          </button>
        )}
        render={(it) => (
          <div className="flex items-center gap-3 min-w-0">
            {it.image
              ? <img src={it.image} alt="" className="h-12 w-12 object-cover border border-[#E5DDD3] rounded shrink-0" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
              : <span className="h-12 w-12 grid place-items-center border border-dashed border-[#E5DDD3] rounded shrink-0 text-[10px] text-[#897F71]">no image</span>}
            <div className="min-w-0">
              <p className="admin-h2 truncate">{it.title || <span className="text-[#897F71] italic">(untitled)</span>}</p>
              <p className="text-xs text-[#897F71] flex items-center gap-2 flex-wrap mt-0.5">
                <span>{it.category || "— no category —"}</span>
                <span>·</span>
                {it.published === false
                  ? <span className="px-1.5 py-0.5 rounded bg-[#FFF1EE] text-[#B5251A] font-semibold">DRAFT</span>
                  : <span className="px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 font-semibold">PUBLISHED</span>
                }
              </p>
            </div>
          </div>
        )}
      />
    </>
  );
}

// ===== Vendors =====
function VendorsAdmin() {
  const [cats, setCats] = useState([]);
  useEffect(() => { axios.get(`${API}/vendor-categories`).then((r) => setCats(r.data || [])); }, []);
  const catNames = cats.map((c) => c.name);
  return (
    <>
      <CrudList
        title="Vendor categories"
        path="vendor-categories"
        requiredKey="name"
        fields={[{ key: "name", label: "Category name" }, { key: "order", label: "Order", type: "number" }]}
        render={(it) => <span className="admin-h2">{it.name}</span>}
      />
      <CrudList
        title="Vendors"
        path="vendors"
        requiredKey="name"
        getCategories={() => catNames}
        sortable
        fields={[
          { key: "name", label: "Name" },
          { key: "category", label: "Category" },
          { key: "description", label: "Description", type: "textarea" },
          { key: "website", label: "Website URL" },
          { key: "instagram", label: "Instagram URL" },
          { key: "image", label: "Image / logo", type: "image" },
          { key: "order", label: "Order", type: "number" },
        ]}
        render={(it) => (
          <div className="flex items-center gap-3 min-w-0">
            {it.image && <img src={it.image} alt="" className="h-12 w-12 object-cover border border-[#E5DDD3] rounded shrink-0" />}
            <div className="min-w-0"><p className="admin-h2 truncate">{it.name}</p><p className="text-xs text-[#897F71]">{it.category}</p></div>
          </div>
        )}
      />
    </>
  );
}

// ===== Services =====
function ServicesAdmin() {
  return (
    <CrudList
      title="Services"
      path="services"
      requiredKey="title"
      fields={[
        { key: "title", label: "Title" },
        { key: "tagline", label: "Tagline" },
        { key: "description", label: "Description", type: "textarea" },
        { key: "icon", label: "Icon (Heart, Sparkles, Camera, Scissors)" },
        { key: "inclusions", label: "Inclusions (one per line)", type: "list" },
        { key: "order", label: "Order", type: "number" },
      ]}
      render={(it) => <div><p className="admin-h2">{it.title}</p><p className="text-xs text-[#897F71]">{it.tagline}</p></div>}
    />
  );
}

// ===== Testimonials =====
function TestimonialsAdmin() {
  return (
    <CrudList
      title="Testimonials"
      path="testimonials"
      requiredKey="name"
      fields={[
        { key: "name", label: "Name" },
        { key: "role", label: "Role / context" },
        { key: "quote", label: "Quote", type: "textarea" },
        { key: "order", label: "Order", type: "number" },
      ]}
      render={(it) => <div><p className="admin-h2">{it.name}</p><p className="text-sm text-[#2A2520] line-clamp-2">“{it.quote}”</p></div>}
    />
  );
}

// ===== Instagram =====
function InstagramAdmin() {
  return (
    <CrudList
      title="Instagram (curated)"
      path="instagram"
      requiredKey="image"
      fields={[
        { key: "image", label: "Image", type: "image" },
        { key: "caption", label: "Caption" },
        { key: "link", label: "Link to Instagram post" },
        { key: "order", label: "Order", type: "number" },
      ]}
      render={(it) => (
        <div className="flex items-center gap-3 min-w-0">
          {it.image && <img src={it.image} alt="" className="h-12 w-12 object-cover border border-[#E5DDD3] rounded shrink-0" />}
          <div className="min-w-0"><p className="text-sm truncate">{it.caption || "(no caption)"}</p></div>
        </div>
      )}
    />
  );
}

// ===== Enquiries =====
function EnquiriesAdmin() {
  const { auth } = useAuth();
  const [items, setItems] = useState([]);
  const load = () => axios.get(`${API}/admin/enquiries`, auth()).then((r) => setItems(r.data || []));
  useEffect(() => { load(); }, []);
  const del = async (id) => { if (!window.confirm("Delete enquiry?")) return; await axios.delete(`${API}/admin/enquiries/${id}`, auth()); load(); };
  return (
    <Section title={`Enquiries (${items.length})`}>
      {items.length === 0 ? <p className="text-[#897F71]">No enquiries yet.</p> : (
        <div className="space-y-3">
          {items.map((e) => (
            <article key={e.id} className="border border-[#E5DDD3] bg-white rounded-md p-4" data-testid={`enquiry-${e.id}`}>
              <div className="flex flex-wrap justify-between gap-3">
                <div><h3 className="admin-h2">{e.name}</h3><p className="text-sm text-[#897F71]">{e.email} {e.phone ? `· ${e.phone}` : ""}</p></div>
                <div className="text-right text-xs text-[#897F71]">
                  <p>{new Date(e.created_at).toLocaleString()}</p>
                  <p className="mt-1" data-testid={`enquiry-${e.id}-email-status`}>
                    <span title="Notification email to Rachel">Admin: {e.admin_email_sent ?? e.email_sent ? "✓" : "✗"}</span>
                    {"  ·  "}
                    <span title="Confirmation email to enquirer">Customer: {e.customer_email_sent ? "✓" : "✗"}</span>
                  </p>
                </div>
              </div>
              <div className="mt-3 grid gap-2 md:grid-cols-3 text-sm">
                <div><b>Event:</b> {e.event_type || "—"}</div><div><b>Date:</b> {e.event_date || "—"}</div><div><b>Location:</b> {e.location || "—"}</div>
                <div><b>People:</b> {e.people_count || "—"}</div><div><b>Service:</b> {e.service_type || "—"}</div><div><b>Appointment:</b> {e.appointment_type || "—"}</div>
              </div>
              {e.message && <p className="mt-3 whitespace-pre-wrap border-t border-[#E5DDD3] pt-3 text-[#2A2520]">{e.message}</p>}
              <button className="admin-btn danger mt-3" onClick={() => del(e.id)}>Delete</button>
            </article>
          ))}
        </div>
      )}
    </Section>
  );
}

// ===== Layout =====
const links = [
  { to: "/admin", label: "Dashboard", icon: FileText, end: true },
  { to: "/admin/content", label: "Page content", icon: FileText },
  { to: "/admin/services", label: "Services", icon: Heart },
  { to: "/admin/service-pricing", label: "Service Pricing", icon: DollarSign },
  { to: "/admin/portfolio", label: "Portfolio", icon: ImageIcon },
  { to: "/admin/vendors", label: "Vendors", icon: Users },
  { to: "/admin/testimonials", label: "Testimonials", icon: Quote },
  { to: "/admin/instagram", label: "Instagram", icon: Camera },
  { to: "/admin/enquiries", label: "Enquiries", icon: Inbox },
];

export default function AdminApp() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  if (user === null) return <p className="p-10">Loading…</p>;
  if (user === false) return <Navigate to="/admin/login" replace />;
  return (
    <div className="admin-shell" data-testid="admin-shell">
      <header className="bg-white border-b border-[#E5DDD3]">
        <div className="mx-auto max-w-7xl px-5 md:px-8 py-3 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <img src="/brand/rmorrison-logo.png" alt="RMorrison" className="h-9 w-auto" />
            <span className="admin-eyebrow ml-2 hidden sm:inline">Admin</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="admin-eyebrow hidden md:inline">{user?.email}</span>
            <button onClick={() => { logout(); nav("/admin/login"); }} className="admin-btn ghost" data-testid="logout-btn"><LogOut className="h-4 w-4" /> Sign out</button>
          </div>
        </div>
      </header>
      <div className="mx-auto max-w-7xl px-4 md:px-6 py-6 grid gap-6 md:grid-cols-[220px_1fr]">
        <nav className="admin-card p-3 h-fit sticky top-4 self-start">
          <ul className="space-y-0.5">
            {links.map((l) => (
              <li key={l.to}>
                <NavLink to={l.to} end={l.end} className={({ isActive }) => `flex items-center gap-2.5 px-3 py-2 rounded-md text-sm font-semibold transition-colors ${isActive ? 'bg-[#FFF1EC] text-coral' : 'text-[#2A2520] hover:bg-[#F5EFE7]'}`} data-testid={`admin-nav-${l.to.split('/').pop() || 'dashboard'}`}>
                  <l.icon className="h-4 w-4" /> {l.label}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>
        <main className="min-w-0">
          <AdminErrorBoundary>
            <Routes>
              <Route index element={<Dashboard />} />
              <Route path="content" element={<ContentEditor />} />
              <Route path="services" element={<ServicesAdmin />} />
              <Route path="service-pricing" element={<ServicePricingAdmin />} />
              <Route path="portfolio" element={<PortfolioAdmin />} />
              <Route path="vendors" element={<VendorsAdmin />} />
              <Route path="testimonials" element={<TestimonialsAdmin />} />
              <Route path="instagram" element={<InstagramAdmin />} />
              <Route path="enquiries" element={<EnquiriesAdmin />} />
            </Routes>
          </AdminErrorBoundary>
        </main>
      </div>
    </div>
  );
}
